import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import moment from 'moment';
import numeral from 'numeral';
import { Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';

const URL_PROJETO_BORRACHA = 'https://project.veja-store.com/pt_br/single/rubber';

function Sobre() {
  const [numeros, setNumeros] = useState(null);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);

  useEffect(() => {
    async function fetchNumeros() {
      const { data } = await axios.get(
        `${process.env.REACT_APP_SERVER}geo-service/dash/big_numbers`,
      );
      setNumeros({ familias: data.producers, associacoes: data.associacoes });
    }

    async function fetchStatus() {
      const { data } = await axios.get(
        `${process.env.REACT_APP_SERVER}geo-service/dash/status`,
      );
      if (data && data.last_db_update) {
        setUltimaAtualizacao(moment(parseFloat(data.last_db_update)).format('DD/MM/YYYY'));
      }
    }

    fetchNumeros();
    fetchStatus();
  }, []);

  return (
    <Pagina>
      <Cabecalho>
        <Sobretitulo>Vert Fair Trade · Sobre</Sobretitulo>
        <h1>Sobre a plataforma</h1>
        <p>
          Monitoramento do risco de desmatamento nas áreas das famílias que
          fornecem borracha da Amazônia para a VEJA.
        </p>
      </Cabecalho>

      <Secao>
        <h2>Apresentação</h2>
        <p>
          Esta plataforma de monitoramento tem como principal objetivo avaliar o
          risco de desmatamento da cadeia de fornecimento de borracha da V. Fair
          Trade (VFT). Ela considera as famílias que fornecem borracha para a VFT
          na Amazônia Legal, por meio de associações distribuídas em Unidades de
          Conservação, Assentamentos Rurais e áreas particulares.
        </p>
        <p>
          A missão da plataforma é apoiar a gestão da cadeia de fornecimento da
          empresa, indicando as áreas de maior e menor risco de desmatamento,
          além de gerar análises automatizadas dos alertas de desmatamento nas
          regiões de interesse e de atuação da VFT.
        </p>
      </Secao>

      <Secao>
        <h2>A borracha da Amazônia na VEJA</h2>
        <p>
          A VEJA compra borracha nativa da Amazônia desde 2004, extraída por
          seringueiros a partir do látex das seringueiras da floresta. Pagar um
          preço justo pela borracha aumenta o valor econômico da floresta em pé e
          ajuda a protegê-la.
        </p>

        <Numeros>
          <Numero>
            <strong>2004</strong>
            <span>início das compras de borracha amazônica</span>
          </Numero>
          <Numero>
            <strong>4.382 t</strong>
            <span>de borracha amazônica adquiridas desde 2004</span>
          </Numero>
          <Numero>
            <strong>{numeros ? numeral(numeros.familias).format('0,0') : '...'}</strong>
            <span>
              famílias fornecedoras em{' '}
              {numeros ? numeros.associacoes : '...'} associações e cooperativas
              acompanhadas nesta plataforma
            </span>
          </Numero>
          <Numero>
            <strong>abr–nov</strong>
            <span>temporada de extração do látex</span>
          </Numero>
        </Numeros>

        <Marcos>
          <li>
            <strong>Reserva Extrativista Chico Mendes</strong>
            <span>
              Em Xapuri, no Acre, é uma das origens da borracha. O látex é
              extraído seguindo um circuito autorizado, que permite a regeneração
              das árvores.
            </span>
          </li>
          <li>
            <strong>2007 · Amopreab</strong>
            <span>
              Parceria com a associação que coordena a produção e o pagamento dos
              seringueiros na Reserva Chico Mendes.
            </span>
          </li>
          <li>
            <strong>2019 · Protocolo para Borracha Sustentável</strong>
            <span>
              Desenvolvido com o fundo Partnership for Forests (P4F), prevê um
              sistema de monitoramento das taxas de desmatamento e bônus que
              incentivam a conservação da floresta. Acompanhar esse desmatamento é
              o papel desta plataforma.
            </span>
          </li>
          <li>
            <strong>2019 · Fair for Life</strong>
            <span>
              Certificação de comércio justo apoiada pela Ecocert: relações
              comerciais diretas e de longo prazo, preço justo e boas condições de
              trabalho.
            </span>
          </li>
        </Marcos>

        <Fonte>
          Fonte: VEJA, projeto da borracha.{' '}
          <a href={URL_PROJETO_BORRACHA} target="_blank" rel="noopener noreferrer">
            Saiba mais no site da VEJA <FontAwesomeIcon icon={faExternalLinkAlt} />
          </a>
        </Fonte>
      </Secao>

      <Secao>
        <h2>Metodologia</h2>
        <p>
          A plataforma utiliza dados oficiais de desmatamento do Sistema PRODES e
          alertas do Sistema DETER, ambos coordenados pelo Instituto Nacional de
          Pesquisas Espaciais (INPE), e analisa a série histórica desde 2008.
        </p>

        <TabelaFontes>
          <thead>
            <tr>
              <th scope="col">Fonte</th>
              <th scope="col">O que mostra</th>
              <th scope="col">Como é usada aqui</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>PRODES</strong> (INPE)</td>
              <td>Desmatamento anual consolidado. O ano PRODES vai de agosto a julho.</td>
              <td>Anos fechados no painel, no mapa e no cálculo de risco.</td>
            </tr>
            <tr>
              <td><strong>DETER</strong> (INPE)</td>
              <td>Alertas de desmatamento e cicatrizes de queimada, publicados ao longo do ano.</td>
              <td>
                Ano corrente (parcial) e camada de queimadas do mapa. Quando o
                PRODES do ano sai, ele substitui os alertas de desmatamento.
              </td>
            </tr>
          </tbody>
        </TabelaFontes>

        <Lista>
          <li>
            <strong>Risco das famílias:</strong> considera o desmatamento
            acumulado nos últimos 3 anos nas áreas de influência de cada família.
          </li>
          <li>
            <strong>Atualização:</strong> os dados são buscados no INPE a cada três
            meses, nos dias 15 de janeiro, abril, julho e outubro.
            {ultimaAtualizacao && ` Última atualização: ${ultimaAtualizacao}.`}
          </li>
        </Lista>

        <Aviso>
          A plataforma não vincula nem atribui a responsabilidade pelo
          desmatamento verificado às famílias extrativistas. Ela apresenta uma
          análise de risco de desmatamento a partir da série histórica de dados.
        </Aviso>
      </Secao>

      <Secao>
        <h2>Como ler os gráficos do Painel</h2>
        <Graficos>
          <dt>Total</dt>
          <dd>
            Desmatamento anual na área de influência da cadeia de fornecimento da
            VEJA (hectares por ano).
          </dd>
          <dt>Por microrregião</dt>
          <dd>Desmatamento de um ano em cada microrregião de influência, por associação.</dd>
          <dt>Por associação</dt>
          <dd>
            Desmatamento anual de cada associação ligada à cadeia de fornecimento.
            Pode haver contagem dupla quando associações compartilham áreas.
          </dd>
          <dt>Média por família</dt>
          <dd>Média de desmatamento por família fornecedora (hectares por família).</dd>
          <dt>Por município</dt>
          <dd>Desmatamento anual nos municípios de atuação.</dd>
          <dt>Por categoria fundiária</dt>
          <dd>Desmatamento anual em cada categoria fundiária de atuação.</dd>
        </Graficos>
        <p className="nota">
          Em todos os gráficos, o ano corrente aparece em tom claro: ele é
          parcial e usa só os alertas do DETER.
        </p>
      </Secao>
    </Pagina>
  );
}

const Pagina = styled(Container)`
  max-width: 900px;
  margin: 20px auto 40px;
  font-family: var(--vft-font);
`;

const Cabecalho = styled.header`
  margin-bottom: 24px;

  h1 {
    margin: 0 0 8px;
    font-size: 28px;
    font-weight: 700;
  }
  p {
    margin: 0;
    font-size: 16px;
    color: var(--vft-muted);
  }
`;

const Sobretitulo = styled.div`
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--vft-muted);
`;

const Secao = styled.section`
  margin-bottom: 20px;
  padding: 24px;
  background: var(--vft-surface);
  border: 1px solid var(--vft-border);
  border-radius: 12px;
  font-size: 15px;
  line-height: 1.6;

  h2 {
    margin: 0 0 12px;
    font-size: 20px;
    font-weight: 700;
  }
  p {
    margin: 0 0 12px;
  }
  .nota {
    margin: 12px 0 0;
    font-size: 13px;
    color: var(--vft-muted);
  }
`;

const Numeros = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin: 16px 0 20px;
`;

const Numero = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px;
  background: var(--vft-subtle);
  border: 1px solid var(--vft-border);
  border-radius: 10px;

  strong {
    font-size: 26px;
    line-height: 1.1;
    font-weight: 700;
    color: var(--vft-ink);
  }
  span {
    font-size: 13px;
    line-height: 1.4;
    color: var(--vft-muted);
  }
`;

const Marcos = styled.ul`
  margin: 0 0 16px;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    flex-direction: column;
    padding: 12px 0 12px 16px;
    border-left: 2px solid var(--vft-border-strong);
  }
  li + li {
    margin-top: 4px;
  }
  strong {
    color: var(--vft-ink);
  }
  span {
    color: var(--vft-text);
  }
`;

const Fonte = styled.p`
  font-size: 13px;
  color: var(--vft-muted);

  a {
    font-weight: 600;
  }
  svg {
    margin-left: 4px;
    font-size: 11px;
  }
`;

const TabelaFontes = styled.table`
  width: 100%;
  margin: 12px 0 16px;
  border-collapse: collapse;
  font-size: 14px;

  th {
    padding: 8px 10px;
    border-bottom: 1px solid var(--vft-border);
    text-align: left;
    font-size: 13px;
    font-weight: 600;
    color: var(--vft-muted);
  }
  td {
    padding: 10px;
    border-bottom: 1px solid var(--vft-hover);
    vertical-align: top;
  }
`;

const Lista = styled.ul`
  margin: 0 0 16px;
  padding-left: 20px;

  li + li {
    margin-top: 6px;
  }
`;

const Aviso = styled.div`
  padding: 14px 16px;
  background: var(--vft-warning-soft);
  border: 1px solid var(--vft-warning-border);
  border-radius: 10px;
  font-size: 14px;
  color: var(--vft-ink);
`;

const Graficos = styled.dl`
  display: grid;
  grid-template-columns: minmax(140px, 200px) 1fr;
  gap: 8px 16px;
  margin: 0;

  dt {
    font-weight: 600;
    color: var(--vft-ink);
  }
  dd {
    margin: 0;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;

    dd {
      margin-bottom: 8px;
    }
  }
`;

export default Sobre;
